// useModuleWalkthrough.tsx
//
// One-stop wrapper around the walkthrough + quiz + chooser dialog wiring that
// every module needs. A page only has to provide its own steps, questions,
// transition logic, and which modals to close — everything else (state,
// auto-start, backend completion, post-walkthrough quiz, "already finished"
// chooser) lives here.
//
// Reference implementation: src/features/EKonsulta/patient-registrations/index.tsx
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { Lightbulb, PlayCircleOutline, Psychology } from "@mui/icons-material";
import { useWalkthrough, WalkthroughStep } from "./useWalkthrough";
import Walkthrough from "./walkthrough";
import Quiz from "../quiz/quiz";
import { QuizQuestion } from "../quiz/useQuiz";
import {
  useGetUserWalkthroughTrack,
  useUpdateUserWalkthrough,
} from "./api/hooks";
import { WalkthroughModel } from "./api/types";
import queryClient from "../../query-client";
import { useWalkthroughCoordination } from "./walkthrough-coordination";

// Same union the backend mutation accepts. Bumping this keeps the hook and
// `useUpdateUserWalkthrough` in lockstep — TypeScript yells if they drift.
export type WalkthroughTrackKey =
  | "Clients"
  | "Consultation"
  | "ConsultationForms"
  | "Facilities"
  | "FacilityUsers"
  | "FollowUpMedicine"
  | "GroupAdmins"
  | "HealthScreening"
  | "HealthScreeningForms"
  | "Home"
  | "LandingPage"
  | "ManageUserAccess"
  | "PatientRegistration"
  | "Products"
  | "Reports"
  | "Transmittals"
  | "BizboxUsers"
  | "Notifications"
  | "ManageAccessability"
  | "Dashboard"
  | "Sessions"
  | "SecuritySettings"
  | "FacilitiesAdmin"
  | "ProductsAdmin"
  | "FacilityUsersAdmin";

// Maps a track key to the matching boolean field on `WalkthroughModel`. Home
// isn't tracked yet (no field exists), so it always reports "not finished".
const isModuleFinished = (
  track: WalkthroughModel | undefined,
  key: WalkthroughTrackKey,
): boolean => {
  if (!track) return false;
  switch (key) {
    case "PatientRegistration":
      return track.isPRPageFinished;
    case "HealthScreening":
      return track.isHSPageFinished;
    case "HealthScreeningForms":
      return track.isHSFormsFinished;
    case "Consultation":
      return track.isConsPageFinished;
    case "ConsultationForms":
      return track.isConsFormsFinished;
    case "FollowUpMedicine":
      return track.isFollowUpMedicineFinished;
    case "Transmittals":
      return track.isTransmittalFinished;
    case "ManageUserAccess":
      return track.isManageUserAccessFinished;
    case "Reports":
      return track.isReportsPageFinished;
    case "Clients":
      return track.isClientsFinished;
    case "Facilities":
      return track.isFacilitiesFinished;
    case "FacilityUsers":
      return track.isFacilityUsersFinished;
    case "GroupAdmins":
      return track.isGroupAdminsFinished;
    case "BizboxUsers":
      return track.isBizboxUsersFinished;
    case "Notifications":
      return track.isNotificationFinished;
    case "ManageAccessability":
      return track.isManageAccessabilityFinished;
    case "Dashboard":
      return track.isDashBoardFinished;
    case "Home":
      return track.isHomeFinished;
    case "Sessions":
      return track.isSessionFinished;
    case "SecuritySettings":
      return track.isSecuritySettingsFinished;
    case "LandingPage":
      return track.isLandingPageFinished;
    case "Products":
      return track.isProductsFinished;
    case "FacilityUsersAdmin":
      return track.isFacilityUsersFinished;
    case "FacilitiesAdmin":
      return track.isFacilitiesFinished;
    case "ProductsAdmin":
      return track.isProductsFinished;
    default:
      return false;
  }
};

export interface UseModuleWalkthroughOptions {
  steps: WalkthroughStep[];
  questions: QuizQuestion[];

  // Persistence — localStorage key for resume-on-skip and a version to bump
  // when steps are reordered/inserted/removed so stale indexes restart cleanly.
  storageKey: string;
  storageVersion?: number;

  // Backend completion key. Drives both the mutation payload and the
  // "already finished?" check that decides whether the lightbulb opens the
  // chooser or starts the walkthrough.
  trackKey: WalkthroughTrackKey;

  // Gates auto-start AND the "already finished" check. Pass something like
  // `!isInternalUser && isMainActive` so internal staff and sub-routes don't
  // trigger the tour.
  isEnabled: boolean;

  // Transition hook — fires AFTER the user advances past step `index`, so it
  // sets up UI for step `index + 1`. Same contract as the existing
  // useWalkthrough `onNext` callback.
  onNext: (step: WalkthroughStep, index: number) => void | Promise<void>;

  // Optional backward-navigation hook — fires BEFORE the step decrements,
  // receiving the step and index being LEFT (going backward). Mirror of onNext.
  onPrev?: (step: WalkthroughStep, index: number) => void | Promise<void>;

  // Optional start hook — fires once when a FRESH walkthrough begins (no
  // saved progress). Use it to set up UI state the first step assumes (e.g.
  // a specific tab, an initial scroll position). Not called on resume — the
  // resume path replays onNext for (saved - 1) instead.
  onStart?: (step: WalkthroughStep, index: number) => void | Promise<void>;

  // Tear-down hook — close every menu/modal the walkthrough may have opened.
  // Called from all the end paths (finish → quiz, "Skip for now", "Don't
  // show again"). Required because the hook doesn't know what UI the page
  // opened during transitions.
  onCloseAll: () => void;

  // Last-step CTA label. Defaults to "Take Quiz" since the quiz flow is the
  // default follow-up. Pass "Finish" for modules that skip the quiz.
  finishLabel?: string;

  // Quiz dialog copy.
  quizTitle: React.ReactNode;
  quizIntro?: React.ReactNode;

  // Chooser dialog copy. Displayed when an already-finished user clicks the
  // lightbulb — gives them "Replay walkthrough" vs "Take quiz".
  moduleName: string; // e.g. "Patient Registration" → "Patient Registration Help"
}

const Walkthroughs: React.FC<{
  isActive: boolean;
  currentStep: WalkthroughStep | null;
  currentStepIndex: number;
  totalSteps: number;
  groupInfo: ReturnType<typeof useWalkthrough>["groupInfo"];
  nextStep: () => void;
  previousStep: () => void;
  goToStep?: (index: number) => void;
  isLastStep: boolean;
  finishLabel: string;
  onFinish: () => void;
  onEnd: () => void;
  onDismiss: () => void;
  isBackwardNavigating: React.RefObject<boolean>;
}> = ({
  isActive,
  currentStep,
  currentStepIndex,
  totalSteps,
  groupInfo,
  nextStep,
  previousStep,
  goToStep,
  isLastStep,
  finishLabel,
  onFinish,
  onEnd,
  onDismiss,
  isBackwardNavigating,
}) => {
  if (!isActive) return null;
  return (
    <Walkthrough
      currentStep={currentStep}
      onNext={nextStep}
      onPrevious={previousStep}
      onGoToStep={goToStep}
      onEnd={onEnd}
      onDismiss={onDismiss}
      onFinish={onFinish}
      finishLabel={finishLabel}
      variant="paged"
      currentStepIndex={currentStepIndex}
      totalSteps={totalSteps}
      groupInfo={groupInfo}
      isLastStep={isLastStep}
      isBackwardNavigating={isBackwardNavigating}
    />
  );
};

export const useModuleWalkthrough = (options: UseModuleWalkthroughOptions) => {
  const {
    steps,
    questions,
    storageKey,
    storageVersion = 1,
    trackKey,
    isEnabled,
    onNext,
    onPrev,
    onStart,
    onCloseAll,
    finishLabel = "Take Quiz",
    quizTitle,
    quizIntro,
    moduleName,
  } = options;

  const walkthroughTracker = useGetUserWalkthroughTrack();
  const updateUserWalkthrough = useUpdateUserWalkthrough(trackKey);

  const {
    isWalkthroughActive,
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    groupInfo,
    startWalkthrough,
    nextStep,
    previousStep,
    goToStep,
    endWalkthrough,
    isBackwardNavigating,
  } = useWalkthrough(steps, onNext, onStart, {
    storageKey,
    storageVersion,
  });

  const handlePreviousStep = () => {
    if (onPrev && currentStep !== null && currentStepIndex > 0) {
      onPrev(currentStep, currentStepIndex);
    }
    previousStep();
  };

  const [quizOpen, setQuizOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);

  const markWalkthroughComplete = () => {
    updateUserWalkthrough.mutate();
    queryClient.invalidateQueries({ queryKey: ["walkthrough-tracker"] });
  };

  // "Don't show again" / final-step completion — close UI, persist done,
  // wipe saved progress so re-trigger starts fresh at step 0.
  const handleEndWalkthrough = () => {
    onCloseAll();
    markWalkthroughComplete();
    endWalkthrough({ clearProgress: true });
  };

  // Last-step CTA — same as end, but also opens the quiz dialog after.
  const handleTakeQuiz = () => {
    onCloseAll();
    markWalkthroughComplete();
    endWalkthrough({ clearProgress: true });
    setQuizOpen(true);
  };

  // "Skip for now" — close UI but DO NOT mark finished, so auto-start
  // re-triggers on next visit. clearProgress stays false so we resume.
  const handleDismissWalkthrough = () => {
    onCloseAll();
    endWalkthrough();
  };

  // Lightbulb click — routes to chooser if already finished, otherwise
  // starts the walkthrough.
  const handleLightbulbClick = () => {
    if (!isEnabled) return;
    if (isModuleFinished(walkthroughTracker.data, trackKey)) {
      setChooserOpen(true);
      return;
    }
    startWalkthrough();
  };

  // Fires auto-start at most ONCE per mount. `isEnabled` is in the effect deps
  // so a user who lands on a sub-route first (isEnabled false) still auto-starts
  // when they reach the main page (isEnabled flips true). But some modules
  // derive `isEnabled` from the route (e.g. follow-up-medicine:
  // `isMainActive = pathname === "/follow-up-medicine"`), and the walkthrough
  // itself navigates to a sub-route during its forms steps. Without this guard,
  // dismissing a forms step ("Skip for now") navigates back to main, flips
  // isEnabled false→true, RE-FIRES this effect, and — because "Skip for now"
  // keeps saved progress — auto-resumes the very step the user just left,
  // looping forever. The page unmounts when the user leaves the module, which
  // resets this ref, so "resume on next visit" still works.
  const hasAutoStartedRef = useRef(false);

  // Auto-start once the tracker resolves. Only fires for not-yet-finished
  // users; internal/sub-route users are gated by `isEnabled`. Suppressed while
  // the app-level What's New tour is running (read via getState, NOT a dep) so
  // a page it navigates to doesn't auto-start its own walkthrough underneath —
  // and ending What's New never retroactively starts the page the user is on.
  // Marks the module as finished on the backend immediately so subsequent page
  // loads never auto-start again — the lightbulb is the re-entry point after
  // the first visit.
  useEffect(() => {
    if (
      isEnabled &&
      walkthroughTracker.data &&
      walkthroughTracker.isSuccess &&
      !isModuleFinished(walkthroughTracker.data, trackKey) &&
      !isWalkthroughActive &&
      !hasAutoStartedRef.current &&
      !useWalkthroughCoordination.getState().isExternalTourActive
    ) {
      hasAutoStartedRef.current = true;
      markWalkthroughComplete();
      startWalkthrough();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walkthroughTracker.isSuccess, isEnabled]);

  const isLastStep =
    currentStep?.highlights === steps[steps.length - 1]?.highlights;

  const resolvedQuizIntro =
    quizIntro ??
    `Quick ${questions.length}-question check to confirm what you learned. ~1 minute.`;

  const walkthroughUI = (
    <>
      <Walkthroughs
        isActive={isActive}
        currentStep={currentStep}
        currentStepIndex={currentStepIndex}
        totalSteps={totalSteps}
        groupInfo={groupInfo}
        nextStep={nextStep}
        previousStep={handlePreviousStep}
        goToStep={goToStep}
        isLastStep={isLastStep}
        finishLabel={finishLabel}
        onFinish={handleTakeQuiz}
        onEnd={handleEndWalkthrough}
        onDismiss={handleDismissWalkthrough}
        isBackwardNavigating={isBackwardNavigating}
      />

      <Quiz
        open={quizOpen}
        title={quizTitle}
        introDescription={resolvedQuizIntro}
        questions={questions}
        onClose={() => setQuizOpen(false)}
      />

      <Dialog
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        maxWidth={"xs" as any}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 12px 32px -8px rgba(18,50,87,0.25)",
            width: "90vw",
            maxWidth: 560,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "primary.main",
            fontWeight: 700,
          }}
        >
          <Lightbulb fontSize="small" color="secondary" />
          {moduleName} Help
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.secondary" }}>
            You've already completed the walkthrough. What would you like to
            do?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setChooserOpen(false);
              startWalkthrough();
            }}
            variant="outlined"
            startIcon={<PlayCircleOutline />}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              fontWeight: 600,
            }}
          >
            Replay walkthrough
          </Button>
          <Button
            onClick={() => {
              setChooserOpen(false);
              setQuizOpen(true);
            }}
            variant="contained"
            startIcon={<Psychology />}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              fontWeight: 600,
            }}
          >
            Take quiz
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return {
    // State the page wires into menus/buttons (keepMounted, isWalkthroughActive
    // props, currentStep?.showTableData, etc.)
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    groupInfo,
    isWalkthroughActive,

    // Imperative helpers if the page needs them.
    startWalkthrough,
    nextStep,
    previousStep,
    // Jump directly to any step index. Only wire to Walkthrough via
    // walkthroughUI when the module handles navigation via useEffect
    // (not onNext hooks) — otherwise dialog/modal steps won't set up correctly.
    goToStep,

    // Wire to the lightbulb button's onClick.
    handleLightbulbClick,

    // Render once at the bottom of the page JSX.
    walkthroughUI,
  };
};
